import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Close, Download, Fullscreen, FullscreenExit } from "@mui/icons-material";
import { processesUploadsApi, uploadsApi } from "api";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import mammoth from "mammoth";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

interface FilePreviewDialogProps {
  attachmentId: number | null;
  filename: string;
  onClose: () => void;
  /** Optional: when provided, shows a local file preview without any API call */
  localFile?: File;
  /** Optional: when true, uses uploadsApi instead of processesUploadsApi for fetching the file */
  useUploadsApi?: boolean;
  mainAttachmentId?: number;
}

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"];
const PDF_EXTS = ["pdf"];
const VIDEO_EXTS = ["mp4", "webm", "mov", "avi", "mkv"];
const AUDIO_EXTS = ["mp3", "wav", "ogg", "m4a", "flac"];
const TEXT_EXTS = ["txt", "csv", "json", "xml", "yaml", "yml", "log", "ts", "tsx", "js", "jsx", "html", "css", "java"];
const MD_EXTS = ["md"];
const DOCX_EXTS = ["docx"];
const XLSX_EXTS = ["xlsx", "xls", "ods"];
const PPTX_EXTS = ["pptx"];
const PPT_EXTS = ["ppt"];
const ALL_PPT_EXTS = ["pptx", "ppt"];
const OFFICE_EXTS = ["docx", "xlsx", "xls", "odt", "ods"];

function getExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() ?? "";
}

/**
 * Converts a base64 data-URI (or raw base64) string into a Blob URL.
 */
function base64ToBlobUrl(base64: string, mime: string): string {
  const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;
  const byteChars = atob(base64Data);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mime });
  return URL.createObjectURL(blob);
}

function getMimeType(ext: string): string {
  if (IMAGE_EXTS.includes(ext)) {
    if (ext === "svg") return "image/svg+xml";
    if (ext === "gif") return "image/gif";
    if (ext === "webp") return "image/webp";
    if (ext === "bmp") return "image/bmp";
    return `image/${ext === "jpg" ? "jpeg" : ext}`;
  }
  if (ext === "pdf") return "application/pdf";
  if (ext === "mp4") return "video/mp4";
  if (ext === "webm") return "video/webm";
  if (ext === "mov") return "video/quicktime";
  if (ext === "mp3") return "audio/mpeg";
  if (ext === "wav") return "audio/wav";
  if (ext === "ogg") return "audio/ogg";
  if (ext === "m4a") return "audio/mp4";
  if (ext === "docx") return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (ext === "xlsx" || ext === "xls") return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (ext === "pptx") return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  if (ext === "ppt") return "application/vnd.ms-powerpoint";
  if (ext === "odt") return "application/vnd.oasis.opendocument.text";
  if (ext === "ods") return "application/vnd.oasis.opendocument.spreadsheet";
  return "application/octet-stream";
}

/** Convert a base64 string to an ArrayBuffer */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;
  const binaryStr = atob(base64Data);
  const len = binaryStr.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return bytes.buffer;
}

const FilePreviewDialog: React.FC<FilePreviewDialogProps> = ({
  attachmentId,
  filename,
  onClose,
  localFile,
  useUploadsApi = false,
  mainAttachmentId,
}) => {
  const translations = useTranslations();
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [xlsxSheets, setXlsxSheets] = useState<{ name: string; data: any[][] }[] | null>(null);
  const [officeError, setOfficeError] = useState(false);
  const [rawBase64, setRawBase64] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pptxPreview, setPptxPreview] = useState<{ thumbnailUrl: string | null; slideCount: number } | null>(null);

  const ext = getExtension(filename);
  const open = attachmentId !== null || localFile !== undefined;

  const decodeBase64 = useCallback((base64: string): string => {
    try {
      const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;
      return decodeURIComponent(escape(atob(base64Data)));
    } catch {
      return atob(base64.includes(",") ? base64.split(",")[1] : base64);
    }
  }, []);

  /** Extract PPTX thumbnail using jszip — PPTX is a ZIP with a docProps/thumbnail image */
  const extractPptxPreview = useCallback(async (arrayBuffer: ArrayBuffer) => {
    try {
      const zip = await JSZip.loadAsync(arrayBuffer);
      // Count slides
      const slideCount = Object.keys(zip.files).filter((name) =>
        /^ppt\/slides\/slide\d+\.xml$/.test(name)
      ).length;
      // Thumbnail candidates (Office 2010+ embeds one here)
      const thumbnailCandidates = [
        "docProps/thumbnail.jpeg",
        "docProps/thumbnail.jpg",
        "docProps/thumbnail.png",
        "docProps/thumbnail.emf",
      ];
      let thumbnailUrl: string | null = null;
      for (const candidate of thumbnailCandidates) {
        const entry = zip.file(candidate);
        if (entry) {
          const blob = await entry.async("blob");
          const mime = candidate.endsWith(".png") ? "image/png" : "image/jpeg";
          thumbnailUrl = URL.createObjectURL(new Blob([blob], { type: mime }));
          break;
        }
      }
      setPptxPreview({ thumbnailUrl, slideCount });
    } catch {
      // JSZip failed (corrupt/non-standard ZIP) — show clean placeholder, not an error
      setPptxPreview({ thumbnailUrl: null, slideCount: 0 });
    }
  }, []);

  const readLocalFile = useCallback(async (file: File) => {
    setError(false);
    setOfficeError(false);
    setDocxHtml(null);
    setXlsxSheets(null);
    setMarkdownContent(null);
    setBlobUrl(null);
    setTextContent(null);
    setRawBase64(null);
    setPptxPreview(null);

    if (MD_EXTS.includes(ext)) {
      const text = await file.text();
      setMarkdownContent(text);
      return;
    }

    if (DOCX_EXTS.includes(ext)) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocxHtml(result.value);
      } catch {
        setOfficeError(true);
      }
      return;
    }

    if (XLSX_EXTS.includes(ext)) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheets = workbook.SheetNames.map((name: any) => {
          const sheet = workbook.Sheets[name];
          const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
          return { name, data };
        });
        setXlsxSheets(sheets);
      } catch {
        setOfficeError(true);
      }
      return;
    }

    if (PPTX_EXTS.includes(ext)) {
      const arrayBuffer = await file.arrayBuffer();
      await extractPptxPreview(arrayBuffer);
      return;
    }

    if (PPT_EXTS.includes(ext)) {
      // Old binary format — cannot extract with JSZip, show placeholder directly
      setPptxPreview({ thumbnailUrl: null, slideCount: 0 });
      return;
    }

    if (TEXT_EXTS.includes(ext)) {
      const text = await file.text();
      setTextContent(text);
      return;
    }

    if (
      IMAGE_EXTS.includes(ext) ||
      PDF_EXTS.includes(ext) ||
      VIDEO_EXTS.includes(ext) ||
      AUDIO_EXTS.includes(ext) ||
      OFFICE_EXTS.includes(ext)
    ) {
      const objectUrl = URL.createObjectURL(file);
      setBlobUrl(objectUrl);
    }
  }, [ext, extractPptxPreview]);

  const fetchServerFile = useCallback(async (attachmentId: number, parentId?: number) => {
    setLoading(true);
    setError(false);
    setOfficeError(false);
    setDocxHtml(null);
    setXlsxSheets(null);
    setMarkdownContent(null);
    setBlobUrl(null);
    setTextContent(null);
    setRawBase64(null);
    setPptxPreview(null);

    try {
      const api = useUploadsApi ? uploadsApi : processesUploadsApi;
      const endpoint = (useUploadsApi && parentId) 
        ? `/${parentId}/histories/${attachmentId}` 
        : `/${attachmentId}`;
      const { data } = await api.get(endpoint);
      const base64: string = data.blob;
      setRawBase64(base64);

      if (MD_EXTS.includes(ext)) {
        setMarkdownContent(decodeBase64(base64));
        setLoading(false);
        return;
      }

      if (DOCX_EXTS.includes(ext)) {
        try {
          const arrayBuffer = base64ToArrayBuffer(base64);
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setDocxHtml(result.value);
        } catch {
          setOfficeError(true);
        }
        setLoading(false);
        return;
      }

      if (XLSX_EXTS.includes(ext)) {
        try {
          const arrayBuffer = base64ToArrayBuffer(base64);
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const sheets = workbook.SheetNames.map((name: any) => {
            const sheet = workbook.Sheets[name];
            const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
            return { name, data };
          });
          setXlsxSheets(sheets);
        } catch {
          setOfficeError(true);
        }
        setLoading(false);
        return;
      }

      if (PPTX_EXTS.includes(ext)) {
        const arrayBuffer = base64ToArrayBuffer(base64);
        await extractPptxPreview(arrayBuffer);
        setLoading(false);
        return;
      }

      if (PPT_EXTS.includes(ext)) {
        // Old binary format — cannot extract with JSZip, show placeholder directly
        setPptxPreview({ thumbnailUrl: null, slideCount: 0 });
        setLoading(false);
        return;
      }

      if (TEXT_EXTS.includes(ext)) {
        setTextContent(decodeBase64(base64));
        setLoading(false);
        return;
      }

      if (
        IMAGE_EXTS.includes(ext) ||
        PDF_EXTS.includes(ext) ||
        VIDEO_EXTS.includes(ext) ||
        AUDIO_EXTS.includes(ext) ||
        OFFICE_EXTS.includes(ext)
      ) {
        const mime = getMimeType(ext);
        const url = base64ToBlobUrl(base64, mime);
        setBlobUrl(url);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [ext, decodeBase64, extractPptxPreview, useUploadsApi]);

  useEffect(() => {
    if (!open) return;
    if (localFile) {
      readLocalFile(localFile);
    } else if (attachmentId !== null) {
      fetchServerFile(attachmentId, mainAttachmentId);
    }
  }, [open, localFile, attachmentId, mainAttachmentId, readLocalFile, fetchServerFile]);

  // Cleanup blobUrl and pptx thumbnail on close
  useEffect(() => {
    if (!open) {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      if (pptxPreview?.thumbnailUrl) {
        URL.revokeObjectURL(pptxPreview.thumbnailUrl);
        setPptxPreview(null);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = () => {
    if (!rawBase64) return;
    const a = document.createElement("a");
    a.href = rawBase64;
    a.download = filename;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const renderXlsxSheet = (sheet: { name: string; data: any[][] }) => (
    <Box key={sheet.name} sx={{ mb: 3 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "primary.main" }}>
        {sheet.name}
      </Typography>
      <Box sx={{ overflowX: "auto", maxHeight: isFullscreen ? "calc(100vh - 140px)" : "60vh", overflow: "auto" }}>
        <Table size="small" sx={{ minWidth: "max-content" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              {sheet.data[0]?.map((cell: any, i: number) => (
                <TableCell key={i} sx={{ fontWeight: 700, whiteSpace: "nowrap", border: "1px solid #ddd" }}>
                  {String(cell ?? "")}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sheet.data.slice(1).map((row: any[], ri: number) => (
              <TableRow key={ri}>
                {row.map((cell: any, ci: number) => (
                  <TableCell key={ci} sx={{ whiteSpace: "nowrap", border: "1px solid #eee", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {String(cell ?? "")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );

  const renderPreview = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
          <Typography color="error">
            {getTranslation(translations, "generic.error") || "Errore nel caricamento del file."}
          </Typography>
        </Box>
      );
    }

    if (officeError) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1} gap={2} minHeight={200}>
          <Typography color="text.secondary">
            {getTranslation(translations, "attachment.preview.label.not.available") || "Anteprima non disponibile per questo tipo di file."}
          </Typography>
        </Box>
      );
    }

    if (IMAGE_EXTS.includes(ext) && blobUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} sx={{ overflow: "auto", p: 1 }}>
          <img
            src={blobUrl}
            alt={filename}
            style={{ maxWidth: "100%", maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh", objectFit: "contain" }}
          />
        </Box>
      );
    }

    if (PDF_EXTS.includes(ext) && blobUrl) {
      return (
        <Box flex={1} sx={{ minHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh", display: "flex", flexDirection: "column" }}>
          <embed
            src={blobUrl}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{ flex: 1, minHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh" }}
          />
        </Box>
      );
    }

    if (VIDEO_EXTS.includes(ext) && blobUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <video controls style={{ maxWidth: "100%", maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh" }}>
            <source src={blobUrl} type={getMimeType(ext)} />
          </video>
        </Box>
      );
    }

    if (AUDIO_EXTS.includes(ext) && blobUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} p={3}>
          <audio controls style={{ width: "100%" }}>
            <source src={blobUrl} type={getMimeType(ext)} />
          </audio>
        </Box>
      );
    }

    if (MD_EXTS.includes(ext) && markdownContent !== null) {
      return (
        <Box
          flex={1}
          sx={{
            overflow: "auto",
            p: 3,
            bgcolor: "#fafafa",
            borderRadius: 1,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
          }}
        >
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              code({ node, inline, className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || "");
                return !inline && match ? (
                  <SyntaxHighlighter
                    {...props}
                    style={vscDarkPlus as any}
                    language={match[1]}
                    PreTag="div"
                  >
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                ) : (
                  <code {...props} className={className}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </Box>
      );
    }

    if (DOCX_EXTS.includes(ext) && docxHtml !== null) {
      return (
        <Box
          flex={1}
          sx={{
            overflow: "auto",
            p: 3,
            bgcolor: "#fff",
            borderRadius: 1,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
            "& table": { borderCollapse: "collapse", width: "100%" },
            "& td, & th": { border: "1px solid #ddd", padding: "6px 10px" },
            "& th": { backgroundColor: "#f5f5f5", fontWeight: 700 },
          }}
          dangerouslySetInnerHTML={{ __html: docxHtml }}
        />
      );
    }

    if ((XLSX_EXTS.includes(ext)) && xlsxSheets !== null) {
      return (
        <Box
          flex={1}
          sx={{
            overflow: "auto",
            p: 2,
            bgcolor: "#fff",
            borderRadius: 1,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
          }}
        >
          {xlsxSheets.map((sheet) => renderXlsxSheet(sheet))}
        </Box>
      );
    }

    if (ALL_PPT_EXTS.includes(ext) && pptxPreview !== null) {
      const { thumbnailUrl, slideCount } = pptxPreview;
      return (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          sx={{
            overflow: "auto",
            bgcolor: "#f5f5f5",
            borderRadius: 1,
            minHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
            p: 3,
          }}
        >
          {thumbnailUrl ? (
            <>
              <Box
                component="img"
                src={thumbnailUrl}
                alt={filename}
                sx={{
                  maxWidth: "100%",
                  maxHeight: isFullscreen ? "calc(100vh - 160px)" : "55vh",
                  objectFit: "contain",
                  borderRadius: 2,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
                  bgcolor: "#fff",
                }}
              />
              {slideCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {slideCount} {slideCount === 1 ? "slide" : "slides"}
                  {" — "}
                  {getTranslation(translations, "attachment.preview.first.slide") || "Anteprima prima diapositiva"}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#e8f4f8",
                  fontSize: 48,
                }}
              >
                📊
              </Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {filename}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {slideCount > 0
                  ? `${slideCount} ${slideCount === 1 ? "slide" : "slides"}`
                  : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getTranslation(translations, "attachment.preview.label.not.available") || "Anteprima miniatura non disponibile — scarica il file per visualizzarlo."}
              </Typography>
              <FlowentiButton
                variant="outlined"
                size="small"
                onClick={() => handleDownload()}
              >
                {getTranslation(translations, "settings.download.label") || "Scarica"}
              </FlowentiButton>
            </>
          )}
        </Box>
      );
    }

    if (ALL_PPT_EXTS.includes(ext) && pptxPreview === null && !loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }

    if (TEXT_EXTS.includes(ext) && textContent !== null) {
      const languageMap: Record<string, string> = {
        ts: "typescript",
        tsx: "tsx",
        js: "javascript",
        jsx: "jsx",
        json: "json",
        html: "html",
        css: "css",
        xml: "xml",
        yaml: "yaml",
        yml: "yaml",
        csv: "csv",
        log: "log",
        txt: "plaintext",
        java: "java"
      };
      const language = languageMap[ext] || "plaintext";

      return (
        <Box
          flex={1}
          sx={{
            overflow: "auto",
            p: 2,
            bgcolor: "#1E1E1E",
            borderRadius: 1,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
          }}
        >
          <SyntaxHighlighter
            style={vscDarkPlus as any}
            language={language}
            PreTag="div"
            customStyle={{ margin: 0, padding: 0, backgroundColor: "transparent" }}
            showLineNumbers={true}
          >
            {textContent}
          </SyntaxHighlighter>
        </Box>
      );
    }

    // Fallback
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
        gap={2}
        minHeight={200}
      >
        <Typography color="text.secondary">
          {getTranslation(translations, "attachment.preview.label.not.available") || "Anteprima non disponibile per questo tipo di file."}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isFullscreen}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: isFullscreen ? 0 : 2,
          minHeight: isFullscreen ? "100vh" : "60vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} flex={1} noWrap title={filename}>
          {filename}
        </Typography>

        {rawBase64 && (
          <Tooltip title={getTranslation(translations, "settings.download.label") || "Download"}>
            <IconButton size="small" onClick={handleDownload}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={isFullscreen
          ? (getTranslation(translations, "generic.exitfullscreen") || "Esci dalla schermata intera")
          : (getTranslation(translations, "generic.fullscreen") || "Schermata intera")
        }>
          <IconButton size="small" onClick={() => setIsFullscreen((prev) => !prev)}>
            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title={getTranslation(translations, "pm.minimap.close.lable") || "Chiudi"}>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "auto",
        }}
      >
        {renderPreview()}
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewDialog;